"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  PromptInputButton,
  usePromptInputController,
} from "@/components/ai-elements/prompt-input";
import { MicIcon as AnimatedMicIcon } from "@/components/ui/mic";
import { SquareIcon } from "lucide-react";

interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
};

const showUnsupportedBrowserWarning = () => {
  toast.warning("Voice input isn't supported in Firefox.", {
    description:
      "Please switch to Chrome, Edge, or Safari, or contact the Woods Data & AI team to request extended browser support.",
  });
};

export const VoiceInputButton = () => {
  const controller = usePromptInputController();
  const [isListening, setIsListening] = useState(false);
  // True only while the recognizer is actively detecting speech in the audio
  // stream — the signal that confirms the mic is picking up the user's voice.
  const [isHearingSpeech, setIsHearingSpeech] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Accumulates committed (final) transcript text for the current recording
  // session; only ever mutated from within the recognition event handlers,
  // never during render.
  const transcriptBaseRef = useRef("");

  useEffect(
    () => () => {
      recognitionRef.current?.stop();
    },
    []
  );

  const startListening = useCallback(() => {
    const SpeechRecognitionImpl = getSpeechRecognitionCtor();
    if (!SpeechRecognitionImpl) {
      showUnsupportedBrowserWarning();
      return;
    }

    const recognition = new SpeechRecognitionImpl();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    transcriptBaseRef.current = controller.textInput.value.trim();

    recognition.addEventListener("speechstart", () => setIsHearingSpeech(true));
    recognition.addEventListener("speechend", () => setIsHearingSpeech(false));

    recognition.addEventListener("result", (event) => {
      const speechEvent = event as SpeechRecognitionEvent;
      let finalChunk = "";
      let interimChunk = "";
      // Always walk the full results list from 0 rather than from
      // event.resultIndex: WebKit (Safari) frequently reports a stale or
      // pinned resultIndex, which would otherwise cause already-committed
      // final results to be re-counted and re-appended on every callback.
      // Rebuilding finalChunk from scratch each time is idempotent because
      // a given result index's isFinal transcript never changes once set.
      for (let i = 0; i < speechEvent.results.length; i += 1) {
        const result = speechEvent.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          finalChunk += transcript;
        } else {
          interimChunk += transcript;
        }
      }

      // transcriptBaseRef holds only the text that existed before this
      // recording session started; it is not mutated during the session so
      // that finalChunk (rebuilt fresh above) can't be double-counted onto
      // an already-updated base.
      const base = transcriptBaseRef.current;
      // Combine final and interim chunks (rather than showing only one or
      // the other) so the box reflects speech as soon as it's recognized —
      // otherwise a final chunk arriving alongside new interim speech would
      // hide that interim text until the next callback, reading as lag.
      const combined = [base, finalChunk.trim(), interimChunk.trim()]
        .filter(Boolean)
        .join(" ");
      if (finalChunk || interimChunk) {
        controller.textInput.setInput(combined);
      }
    });

    recognition.addEventListener("end", () => {
      setIsListening(false);
      setIsHearingSpeech(false);
      recognitionRef.current = null;
    });

    recognition.addEventListener("error", () => {
      setIsListening(false);
      setIsHearingSpeech(false);
      recognitionRef.current = null;
    });

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [controller]);

  const stopListening = useCallback(() => {
    // Flip the UI state immediately rather than waiting on the "end" event —
    // browsers (notably Safari) can delay firing "end" well after stop() is
    // called, which otherwise leaves the stop button looking unresponsive.
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
    setIsHearingSpeech(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Stop recording when the surrounding form is submitted (Send button or
  // Enter), so a sent message's text isn't reused as the base for the next
  // dictation. This runs before PromptInput's own onSubmit handler, so the
  // just-dictated text is still intact when the message is captured and sent.
  useEffect(() => {
    const form = wrapperRef.current?.closest("form");
    if (!form) {
      return;
    }
    const handleFormSubmit = () => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      transcriptBaseRef.current = "";
      setIsListening(false);
      setIsHearingSpeech(false);
    };
    form.addEventListener("submit", handleFormSubmit);
    return () => form.removeEventListener("submit", handleFormSubmit);
  }, []);

  return (
    <div className="relative inline-flex items-center justify-center" ref={wrapperRef}>
      {isHearingSpeech &&
        [0, 1].map((index) => (
          <div
            className="pointer-events-none absolute inset-0 animate-ping rounded-full border-2 border-destructive/40"
            key={index}
            style={{ animationDelay: `${index * 0.3}s`, animationDuration: "1.2s" }}
          />
        ))}
      <PromptInputButton
        className="relative text-muted-foreground"
        onClick={toggleListening}
        tooltip={{
          content: isListening
            ? isHearingSpeech
              ? "Listening…"
              : "Recording — waiting for speech"
            : "Voice input",
          shortcut: "⌘M",
        }}
      >
        {isListening ? (
          <SquareIcon className="text-destructive" size={16} />
        ) : (
          <AnimatedMicIcon size={16} />
        )}
      </PromptInputButton>
    </div>
  );
};
